import React from "react";
import { ScrollView, StyleSheet, View, ViewProps } from "react-native";
import { TouchableOpacity } from "react-native-gesture-handler";
import { useSelector } from "react-redux";
import { Radio, TagsInput } from "src/components";
import { PdSelect } from "src/components/forms/select";
import { PdSwitch } from "src/components/forms/switch";
import { LogoSmall, PdArrowRight, PdChevronDown, PdChevronUp, PdShopify } from "src/components/Icons";
import { PdText } from "src/components/layout";
import { PdDivider } from "src/components/layout/table";
import { PdModal, PdModalCloseButton, PdModalTitle } from "src/components/overlay";

import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import { tUseModal, useModal } from "src/providers/OverlayProvider";
import { tModalComponentProps } from "src/redux/modals/types";
import { putShopifySettings, putShopifyUnlink } from "src/util/apiNew";
import { useConfirmModal, useShopifyImportProductsModal } from ".";

const paymentStatusOptions = [
  { value: "pending", label: "Pending" },
  { value: "authorized", label: "Authorized" },
  { value: "partially_paid", label: "Partially Paid" },
  { value: "paid", label: "Paid" },
  { value: "partially_refunded", label: "Partially Refunded" },
  { value: "refunded", label: "Refunded" },
  { value: "voided", label: "Voided" },
];

const PdVerticalDivider: React.FC<ViewProps> = ({ style, ...rest }) => (
  <View style={[styles.pdVerticalDivider, style]} {...rest} />
);

const OrdersSyncAdditionalSettings: React.FC = () => {
  const [show, setShow] = React.useState(false);
  const rShopifyOrderSyncDeductTesters = useSelector(
    (state) => state.CompanyReducer.type === "brand" && !!state.CompanyReducer.shopifyOrderSyncDeductTesters,
  );
  const rShopifyOrderSyncFinancialStatus = useSelector((state) =>
    state.CompanyReducer.type === "brand" ? state.CompanyReducer.shopifyOrderSyncFinancialStatus : undefined,
  );
  const rShopifyOrderSyncCommission = useSelector((state) =>
    state.CompanyReducer.type === "brand" ? state.CompanyReducer.shopifyOrderSyncCommission : undefined,
  );
  const rShopifyOrderSyncTags = useSelector((state) =>
    state.CompanyReducer.type === "brand" ? state.CompanyReducer.shopifyOrderSyncTags ?? [] : [],
  );
  const rShopifyInventorySyncMarketplaceName = useSelector(
    (state) => state.CompanyReducer.type === "brand" && !!state.CompanyReducer.shopifyOrderSyncMarketplaceName,
  );

  const toggleShow = () => setShow((prev) => !prev);

  return (
    <>
      {show && (
        <>
          <PdDivider style={styles.my4} />
          <View style={[styles.flexRow, styles.alignCenter]}>
            <View style={[styles.flex1, styles.mr4]}>
              <PdText style={[styles.textXs, styles.textBold, styles.mb2]}>Deduct inventory for testers</PdText>
              <PdText style={styles.textXs}>
                Deduct inventory on Shopify when testers are ordered on The Main Tab.
              </PdText>
            </View>
            <View style={styles.flex1}>
              <Radio
                selectedIndex={rShopifyOrderSyncDeductTesters ? 0 : 1}
                myOptions={["Yes", "No"]}
                myFlow="column"
                labelTextStyle={styles.radioLabel}
                onSelect={(value) => putShopifySettings({ shopifyOrderSyncDeductTesters: value.myIndex === 0 })}
              />
            </View>
          </View>
          <PdDivider style={styles.my4} />
          <View style={[styles.flexRow, styles.zIndex1]}>
            <View style={[styles.flex1, styles.mr4]}>
              <PdText style={[styles.textXs, styles.textBold, styles.mb2]}>Order payment status</PdText>
              <PdText style={styles.textXs}>
                Choose which payment status you want your orders to have on Shopify when they&apos;re sent from The Main
                Tab.
              </PdText>
            </View>
            <View style={styles.flex1}>
              <PdSelect
                value={paymentStatusOptions.find(
                  (status) => status.value === (rShopifyOrderSyncFinancialStatus || "paid"),
                )}
                onChange={(option) => option && putShopifySettings({ shopifyOrderSyncFinancialStatus: option.value })}
                options={paymentStatusOptions}
                isClearable={false}
              />
            </View>
          </View>
          <PdDivider style={styles.my4} />
          <View style={[styles.flexRow, styles.alignCenter]}>
            <View style={[styles.flex1, styles.mr4]}>
              <PdText style={[styles.textXs, styles.textBold, styles.mb2]}>Commission</PdText>
              <PdText style={styles.textXs}>
                Choose how The Main Tab commission will be displayed for your orders on Shopify.
              </PdText>
            </View>
            <View style={styles.flex1}>
              <Radio
                selectedIndex={rShopifyOrderSyncCommission === undefined ? 1 : 0}
                myOptions={["In the notes", "Don't show commission"]}
                myFlow="column"
                labelTextStyle={styles.radioLabel}
                onSelect={(value) =>
                  putShopifySettings({ shopifyOrderSyncCommission: value.myIndex === 0 ? "notes" : undefined })
                }
              />
            </View>
          </View>
          <PdDivider style={styles.my4} />
          <View style={styles.flexRow}>
            <View style={[styles.flex1, styles.mr4]}>
              <PdText style={[styles.textXs, styles.textBold, styles.mb2]}>Order tags</PdText>
              <PdText style={styles.textXs}>
                Choose which tags you would like to apply to your The Main Tab orders on Shopify.
              </PdText>
            </View>
            <View style={styles.flex1}>
              <TagsInput
                value={rShopifyOrderSyncTags.join(",")}
                onChangeTags={(value) => putShopifySettings({ shopifyOrderSyncTags: value })}
                inputStyleNoTags={styles.inputTags}
                inputStyleWithTags={styles.inputTags}
                placeholder="Tags"
                limitTo={300}
                allowSpace
              />
            </View>
          </View>
          <PdDivider style={styles.my4} />
          <View style={[styles.flexRow, styles.alignCenter]}>
            <View style={[styles.flex1, styles.mr4]}>
              <PdText style={[styles.textXs, styles.textBold, styles.mb2]}>Order name</PdText>
              <PdText style={styles.textXs}>
                Choose how you would like your The Main Tab orders to be named on Shopify
              </PdText>
            </View>
            <View style={styles.flex1}>
              <Radio
                selectedIndex={rShopifyInventorySyncMarketplaceName ? 0 : 1}
                myOptions={["The Main Tab order number (e.g. #123456-78)", "Shopify order number (e.g. #1301)"]}
                myFlow="column"
                labelTextStyle={styles.radioLabel}
                onSelect={(value) => putShopifySettings({ shopifyOrderSyncMarketplaceName: value.myIndex === 0 })}
              />
            </View>
          </View>
        </>
      )}
      <PdDivider style={styles.my4} />
      <TouchableOpacity onPress={toggleShow} style={[styles.flexRow, styles.alignCenter]}>
        <PdText style={[styles.textXs, styles.textUnderline]}>Additional Settings</PdText>
        <View style={styles.ml2}>
          {show ? <PdChevronUp style={styles.heightXxs} /> : <PdChevronDown style={styles.heightXxs} />}
        </View>
      </TouchableOpacity>
    </>
  );
};

const ShopifySettingsModal: React.FC<tModalComponentProps> = ({ onClose }) => {
  const shopifyImportProductsModal = useShopifyImportProductsModal();
  const confirmModal = useConfirmModal();
  const rShopifyInventorySync = useSelector(
    (state) => state.CompanyReducer.type === "brand" && !!state.CompanyReducer.shopifyInventorySync,
  );
  const rShopifyOrderSync = useSelector(
    (state) => state.CompanyReducer.type === "brand" && !!state.CompanyReducer.shopifyOrderSync,
  );

  const handleOnClose = () => {
    onClose();
  };

  const handleOnPressImportFromShopify = async () => {
    shopifyImportProductsModal.openAsync();
  };

  const handleOnPressRemoveIntegration = async () => {
    const response = await confirmModal.openAsync({
      title: "Are you sure?",
      description:
        "Removing integration will stop all future syncing to and from Shopify. Your current Shopify integrated inventory will still remain synced to The Main Tab.",
      confirmButtonLabel: "Remove Shopify",
    });
    if (response.status === "closed") return;

    await putShopifyUnlink();

    onClose();
  };

  return (
    <PdModal style={styles.modal}>
      <PdModalCloseButton onPress={handleOnClose} />
      <ScrollView contentContainerStyle={styles.modalBody}>
        <PdModalTitle style={styles.mb5}>Shopify Settings</PdModalTitle>
        <PdText style={styles.mb1}>Welcome to Shopify Integration</PdText>
        <PdText style={styles.mb4}>Here you can sync your Shopify store inventory to The Main Tab.</PdText>

        <View style={[styles.widthFull, styles.mb10]}>
          <View style={[styles.flexRow, styles.alignCenter, styles.mb1]}>
            <PdText style={styles.textBold}>Catalog Import</PdText>
            <PdVerticalDivider style={styles.mx2} />
            <PdShopify style={[styles.heightSm, styles.mr2]} />
            <PdArrowRight style={[styles.heightXxs, styles.mr2]} />
            <LogoSmall style={[styles.heightSm, styles.mrAuto]} />
            <TouchableOpacity onPress={handleOnPressImportFromShopify}>
              <PdText style={[styles.textXs, styles.textUnderline]}>Import From Shopify</PdText>
            </TouchableOpacity>
          </View>
        </View>

        <View style={[styles.widthFull, styles.mb10]}>
          <View style={[styles.flexRow, styles.alignCenter, styles.mb1]}>
            <PdText style={styles.textBold}>Inventory sync</PdText>
            <PdVerticalDivider style={styles.mx2} />
            <PdShopify style={[styles.heightSm, styles.mr2]} />
            <PdArrowRight style={[styles.heightXxs, styles.mr2]} />
            <LogoSmall style={styles.heightSm} />
            <View style={[styles.flexRow, styles.alignCenter, styles.mlAuto]}>
              <PdText style={[styles.textXs, styles.mr2]}>{rShopifyInventorySync ? "On" : "Off"}</PdText>
              <PdSwitch
                value={rShopifyInventorySync}
                onValueChange={(value) => putShopifySettings({ shopifyInventorySync: value })}
                style={styles.heightXs}
              />
            </View>
          </View>
          <PdText style={styles.textXs}>
            Inventory counts on The Main Tab will be automatically kept up to date with the counts on Shopify.
          </PdText>
        </View>
        <View style={[styles.widthFull, styles.mb10]}>
          <View style={[styles.flexRow, styles.alignCenter, styles.mb1]}>
            <PdText style={styles.textBold}>Order sync</PdText>
            <PdVerticalDivider style={styles.mx2} />
            <LogoSmall style={[styles.heightSm, styles.mr2]} />
            <PdArrowRight style={[styles.heightXxs, styles.mr2]} />
            <PdShopify style={styles.heightSm} />
            <View style={[styles.flexRow, styles.alignCenter, styles.mlAuto]}>
              <PdText style={[styles.textXs, styles.mr2]}>{rShopifyOrderSync ? "On" : "Off"}</PdText>
              <PdSwitch
                value={rShopifyOrderSync}
                onValueChange={(value) => putShopifySettings({ shopifyOrderSync: value })}
                style={styles.heightXs}
              />
            </View>
          </View>
          <PdText style={[styles.textXs, styles.mb1]}>
            Orders will be automatically synced to Shopify when they have been accepted on The Main Tab.
          </PdText>
          <PdText style={styles.textXs}>
            Please note, order syncs are only from The Main Tab to Shopify. Changes you make to an order on Shopify will
            not reflect on The Main Tab order.
          </PdText>
          <OrdersSyncAdditionalSettings />
        </View>
        <View style={styles.alignCenter}>
          <PdText style={[styles.textBold, styles.textCenter, styles.mb1]}>Want to unlink your Shopify account?</PdText>
          <PdText style={[styles.textCenter, styles.mb1]}>
            This will fully disconnect your The Main Tab and Shopify accounts. If you prefer to just turn off syncing,
            you can do so above.
          </PdText>
          <TouchableOpacity onPress={handleOnPressRemoveIntegration} style={[styles.flexRow, styles.alignCenter]}>
            <PdText style={[styles.textXs, styles.textUnderline, styles.textCenter]}>Remove Shopify Integration</PdText>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </PdModal>
  );
};

const useShopifySettingsModal = (): tUseModal => {
  return useModal(ShopifySettingsModal);
};

export default useShopifySettingsModal;

const styles = StyleSheet.create({
  alignCenter: {
    alignItems: "center",
  },
  flex1: {
    flex: 1,
  },
  flexRow: {
    flexDirection: "row",
  },
  heightSm: {
    height: 20,
  },
  heightXs: {
    height: 12,
  },
  heightXxs: {
    height: 7,
  },
  inputTags: {
    alignItems: "flex-start",
    borderColor: kColors.gray300,
    borderWidth: 1,
    color: kColors.brandDark,
    fontFamily: "Poppins",
    fontWeight: "300",
    height: "100%",
    letterSpacing: 1.1,
    lineHeight: 25,
    paddingHorizontal: 2 * kStyleConst.kSpacing,
    paddingVertical: 1 * kStyleConst.kSpacing,
  },
  mb1: {
    marginBottom: kStyleConst.kSpacing,
  },
  mb10: {
    marginBottom: kStyleConst.kSpacing * 10,
  },
  mb2: {
    marginBottom: 2 * kStyleConst.kSpacing,
  },
  mb4: {
    marginBottom: 4 * kStyleConst.kSpacing,
  },
  mb5: {
    marginBottom: kStyleConst.kSpacing * 5,
  },
  ml2: {
    marginLeft: 2 * kStyleConst.kSpacing,
  },
  mlAuto: {
    marginLeft: "auto",
  },
  modal: {
    marginHorizontal: 8 * kStyleConst.kSpacing,
    maxWidth: 600,
    width: "100%",
  },
  modalBody: {
    alignItems: "flex-start",
    backgroundColor: kColors.lightBrand,
    paddingHorizontal: 6 * kStyleConst.kSpacing,
    paddingVertical: 8 * kStyleConst.kSpacing,
  },
  mr2: {
    marginRight: 2 * kStyleConst.kSpacing,
  },
  mr4: {
    marginRight: 4 * kStyleConst.kSpacing,
  },
  mrAuto: {
    marginRight: "auto",
  },
  mx2: {
    marginHorizontal: 2 * kStyleConst.kSpacing,
  },
  my4: {
    marginVertical: 4 * kStyleConst.kSpacing,
  },
  pdVerticalDivider: {
    backgroundColor: kColors.gray300,
    height: "100%",
    width: StyleSheet.hairlineWidth,
  },
  radioLabel: {
    fontFamily: "Poppins",
    fontSize: 12,
    fontWeight: "300",
    letterSpacing: 0.8,
  },
  textBold: {
    fontWeight: "500",
  },
  textCenter: {
    textAlign: "center",
  },
  textUnderline: {
    textDecorationLine: "underline",
  },
  textXs: {
    fontSize: 12,
  },
  widthFull: {
    width: "100%",
  },
  zIndex1: {
    zIndex: 1,
  },
});
